import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/fleet`;
const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-ZA'));

export const BulkLastServiceDialog = ({ open, onOpenChange, vehicles, headers, onSaved }) => {
  const [vals, setVals] = useState({});
  useEffect(() => {
    if (open) setVals(Object.fromEntries(vehicles.map((v) => [v.id, v.last_service_set ? String(v.last_service_mileage) : ''])));
  }, [open, vehicles]);
  const save = async () => {
    const items = vehicles.filter((v) => vals[v.id] !== '' && vals[v.id] !== (v.last_service_set ? String(v.last_service_mileage) : ''))
      .map((v) => ({ vehicle_id: v.id, last_service_mileage: parseInt(vals[v.id], 10) }));
    if (!items.length) { onOpenChange(false); return; }
    try {
      const r = await axios.put(`${API}/vehicles-bulk/last-service`, { items }, { headers });
      toast.success(`Last service km saved for ${r.data.updated} vehicle(s)`);
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="bulk-last-service-dialog">
        <DialogHeader><DialogTitle>Set last service km</DialogTitle><DialogDescription>Odometer at each vehicle's last full service.</DialogDescription></DialogHeader>
        <p className="text-sm text-slate-600">Enter the odometer reading at each vehicle's last full service. The next service is due at that reading + the service interval.</p>
        <div className="divide-y border rounded">
          {vehicles.map((v) => (
            <div key={v.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex-1"><div className="font-semibold text-sm">{v.registration}</div><div className="text-xs text-slate-500">Current {n(v.current_mileage)} km</div></div>
              {!v.last_service_set && <Badge className="bg-amber-500">not set</Badge>}
              <Input type="number" min="0" value={vals[v.id] ?? ''} onChange={(e) => setVals({ ...vals, [v.id]: e.target.value })} placeholder="Last service km" className="w-40" data-testid={`last-service-input-${v.registration}`} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-last-service-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const VehicleKmLogDialog = ({ vehicle, onClose, headers }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!vehicle) return;
    setD(null);
    axios.get(`${API}/vehicles/${vehicle.id}/km-log`, { headers }).then((r) => setD(r.data)).catch(() => toast.error('Could not load history'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle]);
  return (
    <Dialog open={!!vehicle} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="vehicle-km-log-dialog">
        <DialogHeader><DialogTitle>{vehicle?.registration} — KM log &amp; problems</DialogTitle><DialogDescription>Shift odometer readings and problems reported by drivers.</DialogDescription></DialogHeader>
        {vehicle && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="rounded bg-slate-100 p-2">This week<br /><b>{n(vehicle.km_week)} km</b></div>
            <div className="rounded bg-slate-100 p-2">This month<br /><b>{n(vehicle.km_month)} km</b></div>
            <div className="rounded bg-slate-100 p-2">Avg per working day<br /><b>{n(vehicle.avg_km_per_day)} km</b></div>
            <div className="rounded bg-slate-100 p-2">Est. service date<br /><b>{vehicle.estimated_service_date || '—'}</b></div>
          </div>
        )}
        {!d ? <p className="text-sm text-slate-500">Loading…</p> : <>
          <h4 className="font-semibold text-sm mt-2">Problems reported ({d.faults.length})</h4>
          <div className="border rounded divide-y text-sm max-h-48 overflow-y-auto">
            {d.faults.map((f) => (
              <div key={f.id} className="px-3 py-1.5 flex flex-wrap gap-2 items-center" data-testid={`km-log-fault-${f.id}`}>
                <span className="text-xs text-slate-500">{(f.reported_at || f.created_at || '').slice(0, 10)}</span>
                <Badge variant="outline" className="capitalize">{f.severity}</Badge>
                <span className="flex-1">{f.description}</span>
                <span className="text-xs text-slate-500">{f.reported_by}{f.mileage ? ` · ${n(f.mileage)} km` : ''}</span>
                <Badge className={f.status === 'resolved' ? 'bg-emerald-600' : 'bg-rose-600'}>{f.status}</Badge>
              </div>
            ))}
            {d.faults.length === 0 && <p className="px-3 py-2 text-slate-500">No problems reported.</p>}
          </div>
          <h4 className="font-semibold text-sm mt-2">KM log from shifts ({d.km_log.length})</h4>
          <div className="border rounded overflow-x-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Driver</th><th className="p-2 text-right">Start</th><th className="p-2 text-right">End</th><th className="p-2 text-right">Distance</th></tr></thead>
              <tbody>{d.km_log.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`km-log-row-${r.id}`}>
                  <td className="p-2">{r.shift_date}</td><td className="p-2">{r.driver_name}</td>
                  <td className="p-2 text-right font-mono">{n(r.start_km)}</td><td className="p-2 text-right font-mono">{n(r.end_km)}</td>
                  <td className={`p-2 text-right font-mono ${r.suspicious ? 'text-rose-600 font-bold' : 'text-emerald-700'}`} title={r.suspicious ? 'Unusually high — check the readings' : ''}>{n(r.distance_km)}{r.suspicious ? ' ⚠' : ''}</td>
                </tr>
              ))}
              {d.km_log.length === 0 && <tr><td colSpan={5} className="p-3 text-slate-500">No shifts recorded for this vehicle.</td></tr>}
              </tbody>
            </table>
          </div>
        </>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
